<template>
  <section>
    <div>
      <div
        class="video-controls"
        id="video-controls"
        :style="videoControlStyle"
      >
        <div class="video-progress">
          <vue-slider
            :value="current"
            :min="start"
            :max="end"
            :tooltip="'active'"
            :use-keyboard="false"
            :tooltip-formatter="formatter"
            :process-style="{ backgroundColor: '#48A0F4' }"
            :tooltip-style="{
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              borderColor: 'rgba(0, 0, 0, 0.6)',
            }"
            :duration="0"
            @change="onChangeSeek($event)"
          >
            <template v-slot:dot="{ focus }">
              <div :class="['play-back-video-seek-dot', { focus }]"></div>
            </template>
          </vue-slider>
          <div class="seek-tooltip" id="seek-tooltip">{{ currentTime }}</div>
        </div>

        <div class="bottom-controls">
          <div class="left-controls">
            <feather-icon
              v-if="!isPlay"
              id="play"
              class="play-button"
              @click="onPlay"
              icon="PlayIcon"
            />
            <feather-icon
              v-else
              id="pause"
              class="pause-button"
              @click="onPlay"
              icon="PauseIcon"
            />
            <div class="volume-controls">
              <feather-icon
                id="volume-button"
                :icon="volumeIcon"
                class="volume-button"
              />
              <input
                class="volume"
                id="volume"
                v-model="volume"
                data-mute="0.5"
                type="range"
                max="1"
                min="0"
                step="0.01"
                @change="onChangeVolume()"
              />
            </div>

            <div class="time">
              <time id="time-elapsed">{{ currentTime }} / {{ endTime }}</time>
            </div>
          </div>

          <div class="right-controls">
            <div id="playback-rate-controls">
              <feather-icon
                id="item-playback-minus-button"
                @click="setFPS(false)"
                icon="MinusCircleIcon"
                class="cursor-pointer"
              />
              <span id="item-playback-rate">{{ playbackRate }}</span>
              <feather-icon
                id="item-playback-plus-button"
                @click="setFPS(true)"
                icon="PlusCircleIcon"
                class="cursor-pointer"
              />
            </div>
            <feather-icon
              id="fullscreen-button"
              @click="onChangeFullscreen"
              :icon="fullscreenIcon"
              class="cursor-pointer"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import moment from "moment-timezone";
import VueSlider from "vue-slider-component";
import "vue-slider-component/theme/default.css";

export default {
  name: "PlayerControl",
  components: {
    VueSlider,
  },
  data() {
    return {
      isPlay: false,
      ended: false,
      isFullscreen: false,
      playbackRateList: [1, 2, 4, 8, 16, 32],
      fps: 1,
      videoProgressValue: 0,
      videoProgressMax: 0,
      volume: 0,
      seek: 0,
      formatter: (v) => `${moment.unix(v).format("HH:mm:ss")}`,
      processStyle: {
        borderColor: "rgba(0, 0, 0, 0.6)",
        backgroundColor: "rgba(0, 0, 0, 0.6)",
      },
      tooltipStyle: {
        borderColor: "rgba(0, 0, 0, 0.6)",
        backgroundColor: "rgba(0, 0, 0, 0.6)",
      },
      videoControlStyle: {
        width: "500px",
        right: 1,
        left: 1,
        bottom: 1,
      },
    };
  },
  props: {
    current: { type: Number, required: false },
    start: { type: Number, required: false },
    end: { type: Number, required: false },
  },
  computed: {
    playbackRate() {
      if (this.fps === 1) return "x1";
      if (this.fps === 2) return "x2";
      if (this.fps === 4) return "x4";
      if (this.fps === 8) return "x8";
      if (this.fps === 16) return "x16";
      if (this.fps === 32) return "x32";
      return "";
    },
    reverse: function () {
      return {
        active: this.getReverse && !this.getPause,
      };
    },
    play: function () {
      return {
        active: !this.getReverse && !this.getPause,
      };
    },
    startTime() {
      return moment.unix(this.start).format("HH:mm:ss");
    },
    currentTime() {
      return moment.unix(this.current).format("HH:mm:ss");
    },
    endTime() {
      return moment.unix(this.end).format("HH:mm:ss");
    },
    volumeIcon() {
      if (this.volume <= 0.01) return "VolumeXIcon";
      if (this.volume >= 0.01 && this.volume <= 0.5) return "Volume1Icon";
      return "Volume2Icon";
    },
    fullscreenIcon() {
      if (!this.isFullscreen) return "MaximizeIcon";
      return "MinimizeIcon";
    },
  },
  methods: {
    init() {
      this.fps = 1;
      this.volume = 0;
    },
    setFPS(isIncrement) {
      let index = this.playbackRateList.indexOf(this.fps);
      if (isIncrement) {
        if (this.fps !== 32) {
          this.fps = this.playbackRateList[index + 1];
        }
      } else {
        if (this.fps !== 1) {
          this.fps = this.playbackRateList[index - 1];
        }
      }
      this.$emit("setFPS", this.fps);
    },
    onPlay() {
      this.isPlay = !this.isPlay;
      this.$emit("onPlay", this.isPlay);
    },
    setPause() {
      this.isPlay = false;
    },
    onChangeVolume() {
      this.$emit("onChangeVolume", this.volume);
    },
    onPause() {
      this.$emit("onPause");
    },
    onChangeSeek(time) {
      this.$emit("onChangeSeek", Number(time));
    },
    onChangeFullscreen() {
      this.isFullscreen = !this.isFullscreen;
      this.$emit("onChangeFullscreen", this.isFullscreen);
      this.videoControlStyle.width = "100%";
      this.videoControlStyle.left = 0;
      this.videoControlStyle.right = 0;
      this.videoControlStyle.bottom = 0;
    },
    setStyleToSmall() {
      this.isFullscreen = !this.isFullscreen;
      this.videoControlStyle.width = "500px";
      this.videoControlStyle.left = "";
      this.videoControlStyle.right = "";
      this.videoControlStyle.bottom = "";
    },
  },
};
</script>

<style lang="scss">
@import "src/assets/scss/aisea/variables";

.video-controls {
  cursor: pointer;
  padding: 10px;
  position: absolute;
  transition: all 0.2s ease;
  background-image: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.3),
    rgba(0, 0, 0, 0.5)
  );
}

.video-controls.hide {
  opacity: 0;
  pointer-events: none;
}

.video-progress {
  position: relative;
  height: 8.4px;
  margin-bottom: 10px;
}

.play-back-video-seek-dot {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background-color: #7367f0;
}

progress {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  border-radius: 2px;
  width: 100%;
  height: 8.4px;
  pointer-events: none;
  position: absolute;
  top: 0;
}

progress::-webkit-progress-bar {
  background-color: #474545;
  border-radius: 2px;
}

progress::-webkit-progress-value {
  background-color: #474545;
  border-radius: 2px;
}

.seek {
  position: absolute;
  top: 0;
  width: 100%;
  cursor: pointer;
  margin: 0;
}

.seek:hover + .seek-tooltip {
  display: block;
}

.seek-tooltip {
  display: none;
  position: absolute;
  top: -50px;
  margin-left: -20px;
  font-size: 12px;
  padding: 3px;
  content: attr(data-title);
  font-weight: bold;
  color: #fff;
  background-color: rgba(0, 0, 0, 0.6);
}

.bottom-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.left-controls {
  display: flex;
  align-items: center;
  color: #fff;
}

.right-controls {
  display: flex;
  align-items: center;
  color: #fff;
}

.volume-controls {
  display: flex;
  align-items: center;
  margin-right: 10px;
}

.volume-controls input {
  width: 100px;
  opacity: 1;
  transition: all 0.4s ease;
}

.volume-controls:hover input,
.volume-controls input:focus {
  width: 100px;
  opacity: 1;
}

.fullscreen-button {
  margin-right: 0;
}

.playback-animation {
  pointer-events: none;
  position: absolute;
  top: 50%;
  left: 50%;
  margin-left: -40px;
  margin-top: -40px;
  width: 80px;
  height: 80px;
  border-radius: 80px;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
}

input[type="range"] {
  -webkit-appearance: none;
  -moz-appearance: none;
  height: 8.4px;
  background: transparent;
  cursor: pointer;
}

input[type="range"]:focus {
  outline: none;
}

input[type="range"]::-webkit-slider-runnable-track {
  width: 100%;
  cursor: pointer;
  border-radius: 1.3px;
  -webkit-appearance: none;
  transition: all 0.4s ease;
}

input[type="range"]::-webkit-slider-thumb {
  height: 16px;
  width: 16px;
  border-radius: 16px;
  background: #7367f0;
  cursor: pointer;
  -webkit-appearance: none;
  margin-left: -1px;
}

input[type="range"]:focus::-webkit-slider-runnable-track {
  background: transparent;
}

input[type="range"].volume {
  height: 5px;
  background-color: #fff;
}

input[type="range"].volume::-webkit-slider-runnable-track {
  background-color: transparent;
}

input[type="range"].volume::-webkit-slider-thumb {
  margin-left: 0;
  height: 14px;
  width: 14px;
  background: #fff;
}

input[type="range"]::-moz-range-track {
  width: 100%;
  height: 8.4px;
  cursor: pointer;
  border: 1px solid transparent;
  background: transparent;
  border-radius: 1.3px;
}

input[type="range"]::-moz-range-thumb {
  height: 14px;
  width: 14px;
  border-radius: 50px;
  border: 1px solid #7367f0;
  background: #7367f0;
  cursor: pointer;
  margin-top: 5px;
}

input[type="range"]:focus::-moz-range-track {
  outline: none;
}

input[type="range"].volume::-moz-range-thumb {
  border: 1px solid #fff;
  background: #fff;
}

.play-button {
  svg {
    width: 24px;
    height: 24px;
    cursor: pointer;
    margin-right: 1rem;
  }
}

.pause-button {
  svg {
    width: 24px;
    height: 24px;
    cursor: pointer;
    margin-right: 1rem;
  }
}

.volume-button {
  svg {
    width: 24px;
    height: 24px;
    cursor: pointer;
    margin-right: 1rem;
  }
}

#playback-rate-controls {
  margin-right: 1rem;
  display: grid;
  grid-template-rows: 40px;
  grid-template-columns: 24px 42px 24px;
  align-content: center;
  justify-content: center;
  justify-items: center;
  align-items: center;
  grid-template-areas: "area-minus area-rate area-plus";
}

#item-playback-minus-button {
  grid-area: area-minus;
  width: 24px;
  height: 24px;
}

#item-playback-rate {
  grid-area: area-rate;
  font-size: 18px;
  font-weight: 500;
  margin-left: 1rem;
  margin-right: 1rem;
}

#item-playback-plus-button {
  grid-area: area-plus;
  width: 24px;
  height: 24px;
}
</style>
